import { invokeApi } from '@/lib/apiClient';
import { supabase } from '@/integrations/supabase/client';

export type StripePlan =
  | 'oposiciones'
  | 'pro_monthly'
  | 'pro_yearly'
  | 'oposiciones_basic_monthly'
  | 'oposiciones_basic_yearly'
  | 'base_comun_monthly'
  | 'base_comun_yearly'
  | 'oposiciones_single_monthly'
  | 'oposiciones_single_yearly'
  | 'oposiciones_multi_monthly'
  | 'oposiciones_multi_yearly';

export interface SubscriptionStatus {
  subscribed: boolean;
  plan: string;
  subscription_end: string | null;
  cancel_at_period_end?: boolean;
}

export async function createCheckoutSession(plan: StripePlan): Promise<string> {
  const { data: { session } } = await supabase.auth.getSession();
  
  if (!session?.access_token) {
    throw new Error('Debes iniciar sesión para continuar');
  }

  const { data, error } = await invokeApi<{ url: string }>('create-checkout', {
    body: { plan },
  });

  if (error) {
    console.error('Error creating checkout session:', error);
    throw new Error(error.message || 'Error al crear la sesión de pago');
  }

  if (!data?.url) {
    throw new Error('No se pudo obtener la URL de pago');
  }

  return data.url;
}

export async function checkSubscription(): Promise<SubscriptionStatus> {
  const { data: { session } } = await supabase.auth.getSession();
  
  if (!session?.access_token) {
    return { subscribed: false, plan: 'free', subscription_end: null };
  }

  const { data, error } = await invokeApi('check-subscription');

  if (error) {
    console.error('Error checking subscription:', error);
    return { subscribed: false, plan: 'free', subscription_end: null };
  }

  const result = data as SubscriptionStatus;

  // Cache in sessionStorage to avoid redundant calls on page reload
  try {
    sessionStorage.setItem('munilex_sub_cache', JSON.stringify({ data: result, timestamp: Date.now() }));
  } catch { /* ignore storage errors */ }

  return result;
}

export async function openCustomerPortal(): Promise<string> {
  const { data: { session } } = await supabase.auth.getSession();
  
  if (!session?.access_token) {
    throw new Error('Debes iniciar sesión para continuar');
  }

  const { data, error } = await invokeApi<{ url: string }>('customer-portal');

  if (error) {
    console.error('Error opening customer portal:', error);
    throw new Error(error.message || 'Error al abrir el portal de pagos');
  }

  if (!data?.url) {
    throw new Error('No se pudo obtener la URL del portal');
  }

  return data.url;
}
